/**
 * Shared time-range picker for every observability surface (design 19 §7,
 * VSNP-1218).
 *
 * The control only SELECTS; `src/utils/timeRange.ts` resolves a selection into
 * concrete query bounds at fetch time (see that file for why the two are
 * apart). Controlled by design — value + onChange, with only the custom-range
 * draft in local state, so nothing is written to state from an effect.
 */

import { useState } from 'react';
import { Calendar, ChevronDown } from 'lucide-react';
import {
  TIME_RANGE_PRESETS,
  type TimeRangePreset,
  type TimeRangeValue,
} from '../../utils/timeRange';

/** `datetime-local` value for an instant, in the browser's own timezone. */
function toLocalInput(ms: number): string {
  const d = new Date(ms - new Date(ms).getTimezoneOffset() * 60000);
  return d.toISOString().slice(0, 16);
}

interface TimeRangeProps {
  value: TimeRangeValue;
  onChange: (value: TimeRangeValue) => void;
  className?: string;
}

export const TimeRange = ({ value, onChange, className = '' }: TimeRangeProps) => {
  const [open, setOpen] = useState(false);
  const [draftFrom, setDraftFrom] = useState(() => value.from ?? toLocalInput(Date.now() - 3600_000));
  const [draftTo, setDraftTo] = useState(() => value.to ?? toLocalInput(Date.now()));

  const current = value.preset === 'custom'
    ? `${value.from ?? ''} → ${value.to ?? ''}`
    : (TIME_RANGE_PRESETS.find((p) => p.key === value.preset)?.label ?? 'Last 1 hour');

  const pick = (preset: Exclude<TimeRangePreset, 'custom'>) => {
    onChange({ preset });
    setOpen(false);
  };

  const applyCustom = () => {
    if (!draftFrom || !draftTo) return;
    onChange({ preset: 'custom', from: draftFrom, to: draftTo });
    setOpen(false);
  };

  return (
    <div className={`relative ${className}`} data-testid="time-range">
      <button
        type="button"
        className="btn-secondary inline-flex items-center gap-2 whitespace-nowrap"
        onClick={() => setOpen((o) => !o)}
        data-testid="time-range-button"
      >
        <Calendar className="h-4 w-4" />
        <span data-testid="time-range-label">{current}</span>
        <ChevronDown className="h-3.5 w-3.5 text-gray-400" />
      </button>

      {open && (
        <>
          {/* Click-away. A plain overlay rather than a document listener: no
              effect, no listener to leak, and it cannot outlive the popover. */}
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} aria-hidden="true" />
          <div className="absolute right-0 z-20 mt-1 w-72 rounded-md border border-gray-200 bg-white p-2 shadow-lg">
            <div className="grid grid-cols-2 gap-1">
              {TIME_RANGE_PRESETS.map((p) => (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => pick(p.key)}
                  data-testid={`time-range-preset-${p.key}`}
                  className={`rounded px-2 py-1.5 text-left text-sm hover:bg-gray-100 ${
                    value.preset === p.key ? 'bg-primary-50 font-medium text-primary-700' : 'text-gray-700'
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
            <div className="mt-2 border-t border-gray-100 pt-2">
              <p className="mb-1 text-xs font-medium text-gray-500">Custom range</p>
              <div className="space-y-1">
                <input
                  type="datetime-local"
                  className="input-base w-full text-xs"
                  value={draftFrom}
                  onChange={(e) => setDraftFrom(e.target.value)}
                  aria-label="Range start"
                />
                <input
                  type="datetime-local"
                  className="input-base w-full text-xs"
                  value={draftTo}
                  onChange={(e) => setDraftTo(e.target.value)}
                  aria-label="Range end"
                />
                <button type="button" className="btn-primary w-full text-xs" onClick={applyCustom}>
                  Apply
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default TimeRange;
